import { Button, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useEffect, useMemo } from "react";
import { useImmer } from "use-immer";
import { useNavigate } from "react-router-dom";

import axios from "axios";

import { GridColDef } from "@mui/x-data-grid";
import toast from "react-hot-toast";
import InfoIcon from "@mui/icons-material/Info";
import EditIcon from "@mui/icons-material/Edit";
import AttachmentIcon from "@mui/icons-material/Attachment";
import dayjs from "dayjs";
import { APP_BASE_URL, JOINT_INSPECION_ASSIGNMENT_API } from "../../Common/EndPoints";
import DataTable from "../DataGrid/DataGrid";

const JointInspectionFixedColumns: GridColDef[] = [
  {
    field: "id",
    headerName: "ID",
    sortable: true,
    minWidth: 150,
    hideable: true,
    flex: 1,
  },
  {
    field: "startedTs",
    headerName: "Created Date",
    minWidth: 150,
    hideable: true,
    flex: 1,
    valueFormatter: (params) => {
      return dayjs(params.value).format("DD-MM-YYYY");
    },
  },
  {
    field: "group",
    headerName: "Customer Group",
    minWidth: 150,
    hideable: true,
    flex: 1,
    valueFormatter: (params) => {
      return params.value?.name;
    },
  },
  {
    field: "scheduleCode",
    headerName: "Schedule Code",
    minWidth: 150,
    hideable: true,
    flex: 1,
    valueGetter: (params) => {
      return params.value?.name;
    },
  },
  {
    field: "assetType",
    headerName: "Asset Type",
    minWidth: 150,
    hideable: true,
    flex: 1,
    valueGetter: (params) => {
      return params.value?.name;
    },
  },
  {
    field: "userRoleNames1",
    headerName: "User Role 1",
    minWidth: 150,
    hideable: true,
    flex: 1,
    valueGetter: (params) => {
      return params.row?.userRole?.[0]?.name;
    },
  },
  {
    field: "formAssignedPrimary",
    headerName: "Form Assigned 1",
    minWidth: 150,
    hideable: true,
    flex: 1,
    valueGetter: (params) => {
      return params.row?.form?.[0]?.name;
    },
  },
  {
    field: "userRoleNames2",
    headerName: "User Role 2",
    minWidth: 150,
    hideable: true,
    flex: 1,
    valueGetter: (params) => {
      return params.row?.userRole?.[1]?.name;
    },
  },
  {
    field: "formAssignedSecondary",
    headerName: "Form Assigned 2",
    minWidth: 150,
    hideable: true,
    flex: 1,
    valueGetter: (params) => {
      return params.row?.form?.[1]?.name;
    },
  },
];

type IState = {
  loading: boolean;
  data: any[];
  totalElements: number;
  showAddEditModal: boolean;
};

const initialState: IState = {
  loading: false,
  data: [],
  totalElements: 0,
  showAddEditModal: false,
};

const parseNavigateData = (row: any) => {
  let data: any = {};
  data.jointInspectionId = row.jointInspectionId;

  data.periodicity = row.periodicity;
  data.dueAssignment = row.dueOffset;
  data.active = row.active;

  data.scheduleCodes = {
    key: row.scheduleCode?.id,
    value: row.scheduleCode?.name,
  };
  data.startDate = dayjs(row.startedTs).format("YYYY-MM-DD");
  data.gearType = {
    id: row.assetType?.id,
    value: row.assetType?.name,
  };

  data.userRoleIdPrimary = {
    id: row.userRole?.[0]?.id,
    value: row.userRole?.[0]?.name,
  };
  data.userRoleIdSecoundary = {
    id: row.userRole?.[1]?.id,
    value: row.userRole?.[1]?.name,
  };

  data.maintenanceFormIdPrimary = {
    key: row.form?.[0]?.id,
    value: row.form?.[0]?.name,
  };
  data.maintenanceFormIdSecoundary = {
    key: row.form?.[1]?.id,
    value: row.form?.[1]?.name,
  };

  data.stationGroup = {
    key: row.group?.id,
    value: row.group?.name,
  };

  return data;
};

const JointInspectionAssignment = () => {
  const [state, setState] = useImmer(initialState);
  const navigate = useNavigate();

  const getJointInspectionTableColumns: () => GridColDef[] = () => {
    const actionCol: GridColDef = {
      field: "editActions",
      headerName: "Action",
      filterable: false,
      minWidth: 150,
      hideable: true,
      flex: 1,
      renderCell: (params) => (
        <>
          <Tooltip placement="bottom" title={<div className="tooltip--title">Edit</div>} disableInteractive>
            <IconButton
              aria-label="Edit"
              onClick={() => {
                try {
                  const data = parseNavigateData(params.row);

                  navigate(`/joint-inspection-assignment-form`, { state: data });
                } catch (error: any) {
                  toast.error(error?.message || "Something went wrong! Try again later!");
                }
              }}
            >
              <EditIcon />
            </IconButton>
          </Tooltip>
        </>
      ),
    };
    return [actionCol, ...JointInspectionFixedColumns];
  };

  const JointInspectionTableColumns = useMemo(() => getJointInspectionTableColumns(), []);

  useEffect(() => {
    getScheduleList();
  }, []);

  const getScheduleList = async () => {
    try {
      setState((prev) => {
        prev.loading = true;
      });
      const url = `${APP_BASE_URL}${JOINT_INSPECION_ASSIGNMENT_API}`;
      const res = await axios.get(url);
      let RESPONSE = res.data.data;
      setState((prev) => {
        prev.data = Object.entries(RESPONSE).map(([key, value]: any) => {
          return { id: key, ...value };
        });
      });
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        toast.error(error?.response?.data?.error?.message || "Something went wrong! Try again later!");
        return;
      }
      toast.error(error?.message || "Something went wrong! Try again later!");
      setState((prev) => {
        prev.data = [];
      });
    } finally {
      setState((prev) => {
        prev.loading = false;
      });
    }
  };

  return (
    <>
      <Stack spacing={1} direction={"row"} justifyContent={"end"}>
        <Button variant="outlined" onClick={() => navigate("/station-group")}>
          Station Group
        </Button>
        <Button
          variant="contained"
          onClick={() => navigate("/joint-inspection-assignment-form")}
          className="create-user-button"
        >
          New Assignment
        </Button>
      </Stack>

      <DataTable
        tableInfo={{
          id: `joint-inspection-table`,
          label: `Joint Inspection`,
          requireAuthSign: false,
          timestamp: { startTs: null, endTs: null },
        }}
        loading={state.loading}
        rows={state.data}
        columns={JointInspectionTableColumns}
        hideExportPDF={true}
      />
    </>
  );
};


export default JointInspectionAssignment;